import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, AlertTriangle, Printer } from "lucide-react";
import api from "@/lib/api";
import { Section } from "@/components/common";
import { STUDIO_INFO } from "@/lib/brand";

function money(n) { return `${Number(n || 0).toFixed(2)} €`; }

export default function PaymentResult() {
  const [q] = useSearchParams();
  const id = q.get("reservation_id");
  const [status, setStatus] = useState("PENDING");
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!id) return;
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/booking/payments/status/${id}`);
        if (stopped) return;
        setStatus(r.data.reservation_status);
        setRes(r.data.reservation);
        tries += 1;
        if (r.data.reservation_status !== "CONFIRMED" && r.data.reservation_status !== "PAYMENT_FAILED" && tries < 20) {
          setTimeout(poll, 2500);
        }
      } catch { if (!stopped) setStatus("ERROR"); }
    };
    poll();
    return () => { stopped = true; };
  }, [id]);

  const confirmed = status === "CONFIRMED";
  const failed = status === "PAYMENT_FAILED" || status === "EXPIRED";

  return (
    <Section className="py-24 min-h-[70vh]">
      {confirmed && res ? (
        <div className="print-area max-w-xl mx-auto">
          <div className="flex items-center gap-3 mb-6 no-print">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Check className="text-emerald-400" size={24} /></div>
            <h1 className="font-display font-black text-2xl text-emerald-400">SESSION CONFIRMÉE</h1>
          </div>
          <div className="card-dark p-8">
            <div className="text-center border-b border-white/10 pb-5 mb-5">
              <div className="font-display font-black text-xl">AL MUSIC RECORDS</div>
              <div className="eyebrow mt-1">Confirmation de réservation</div>
              <div className="font-mono text-2xl text-gold mt-3">{res.booking_number}</div>
            </div>
            {[["Client", `${res.first_name} ${res.last_name}`], ["Prestation", res.service_name], ["Date", res.date], ["Heure", `${res.start_time} - ${res.end_time}`], ["Durée", `${res.duration} h`], ["Prix total", money(res.price_ttc)], ["Acompte payé", money(res.deposit_amount)], ["Solde restant", money(res.balance_amount)]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-1.5"><span className="text-slate-500">{k}</span><span className="text-white">{v}</span></div>
            ))}
            <div className="border-t border-white/10 mt-4 pt-4 text-sm text-slate-400">
              📍 {STUDIO_INFO.name} — {STUDIO_INFO.address}<br />📞 {STUDIO_INFO.phone}
            </div>
          </div>
          <div className="flex gap-3 mt-8 no-print">
            <button onClick={() => window.print()} data-testid="print-recap-btn" className="btn-gold"><Printer size={16} /> Imprimer / PDF</button>
            <Link to="/" className="btn-ghost">Retour à l'accueil</Link>
          </div>
        </div>
      ) : failed ? (
        <div className="max-w-lg mx-auto text-center">
          <AlertTriangle className="text-amber-400 mx-auto" size={40} />
          <h1 className="font-display font-black text-2xl mt-4">Le paiement n'a pas été confirmé</h1>
          <p className="text-slate-400 mt-3">Ton créneau n'est pas encore réservé. Tu peux réessayer.</p>
          <Link to="/booking-studio" className="btn-gold mt-8 inline-flex">Réessayer</Link>
        </div>
      ) : (
        <div className="max-w-lg mx-auto text-center">
          <Loader2 className="animate-spin text-gold mx-auto" size={40} />
          <h1 className="font-display font-bold text-xl mt-4">Vérification du paiement…</h1>
          <p className="text-slate-500 mt-2">Merci de patienter quelques instants.</p>
        </div>
      )}
    </Section>
  );
}
